import React, { useState, useEffect, memo } from 'react';
import { portfolioConfig } from '../config/portfolio';
import { Icon } from './Icons';
import emailjs from '@emailjs/browser';

const ContactInfoCard = memo(({ iconName, title, children, href }) => {
  const content = href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">
      {children}
    </a>
  ) : (
    <p className="text-cyan-400">{children}</p>
  );

  return (
    <div className="flex items-center gap-4 p-4 bg-gray-900/50 rounded-lg hover:bg-gray-900/70 transition-colors duration-300">
      <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
        <Icon name={iconName} className="text-black" />
      </div>
      <div>
        <h4 className="text-white font-semibold">{title}</h4>
        {content}
      </div>
    </div>
  );
});

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [visibleBlocks, setVisibleBlocks] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleBlocks(prev => (prev.includes(index) ? prev : [...prev, index]));
          }
        });
      },
      { threshold: 0.1 }
    );

    const blocks = document.querySelectorAll('.contact-anim-block');
    blocks.forEach(block => observer.observe(block));

    return () => observer.disconnect();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    emailjs
      .send(
        'service_7x7ky2j',
        'template_qswcymg',
        formData,
        'NhzzBbmpx4tjrsvWC'
      )
      .then(() => {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      })
      .catch(() => {
        setIsSubmitting(false);
        setSubmitStatus('error');
      });
  };

  return (
    <section id="contact" className="py-20 bg-gray-800/50">
      <div className="container mx-auto px-8 lg:mb-12">
        <div
          data-index={0}
          className={`contact-anim-block transition-all duration-700 ease-out ${
            visibleBlocks.includes(0) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          } text-center mb-16`}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            Let's Work{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Together
            </span>
          </h2>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              data-index={1}
              className={`contact-anim-block space-y-8 transition-all duration-700 ease-out ${
                visibleBlocks.includes(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >

              {[
                {
                  iconName: 'mail',
                  title: 'Email',
                  href: `mailto:${portfolioConfig.contact.email}`,
                  content: portfolioConfig.contact.email,
                },
                {
                  iconName: 'phone',
                  title: 'Phone',
                  href: `https://api.whatsapp.com/send?phone=${portfolioConfig.contact.phone.replace('+', '')}`,
                  content: portfolioConfig.contact.phone,
                },
                {
                  iconName: 'map-pin',
                  title: 'Location',
                  href: null,
                  content: portfolioConfig.personal.location,
                },
              ].map(({ iconName, title, href, content }) => (
                <ContactInfoCard key={title} iconName={iconName} title={title} href={href}>
                  {content}
                </ContactInfoCard>
              ))}

              <div>
                <p className="text-gray-300 text-lg leading-relaxed mt-2">
                  I'm always open to discussing new opportunities, interesting projects, or just having a chat about technology and development.
                </p>
              </div>
            </div>

            <div
              data-index={3}
              className={`contact-anim-block bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/50 min-h-[400px] flex items-center transition-all duration-700 ease-out ${
                visibleBlocks.includes(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {submitStatus === 'success' ? (
                <div className="w-full text-center">
                  <div className="relative w-20 h-20 mx-auto mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full opacity-0 animate-[fadeIn_0.3s_ease-out_0.1s_forwards]" />
                    <svg
                      className="absolute inset-0 w-full h-full text-white stroke-current stroke-[4px]"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path
                        d="M5 13l4 4L19 7"
                        className="path-animate"
                        style={{ strokeDasharray: 22, strokeDashoffset: 22, animation: 'draw 0.6s ease-out 0.4s forwards' }}
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-gray-300">Thanks for reaching out. I'll get back to you soon!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="w-full space-y-6" noValidate>
                  <div>
                    <label htmlFor="name" className="block text-gray-300 mb-2 font-semibold">
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full rounded-md px-4 py-3 bg-gray-800 border ${
                        errors.name ? 'border-red-500' : 'border-gray-700'
                      } text-white focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                      aria-invalid={errors.name ? 'true' : 'false'}
                      aria-describedby="name-error"
                    />
                    {errors.name && (
                      <p id="name-error" className="mt-1 text-red-500 text-sm">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-300 mb-2 font-semibold">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full rounded-md px-4 py-3 bg-gray-800 border ${
                        errors.email ? 'border-red-500' : 'border-gray-700'
                      } text-white focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                      aria-invalid={errors.email ? 'true' : 'false'}
                      aria-describedby="email-error"
                    />
                    {errors.email && (
                      <p id="email-error" className="mt-1 text-red-500 text-sm">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-gray-300 mb-2 font-semibold">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="3"
                      value={formData.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full rounded-md px-4 py-3 bg-gray-800 border ${
                        errors.message ? 'border-red-500' : 'border-gray-700'
                      } text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none`}
                      aria-invalid={errors.message ? 'true' : 'false'}
                      aria-describedby="message-error"
                    />
                    {errors.message && (
                      <p id="message-error" className="mt-1 text-red-500 text-sm">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 rounded-md font-semibold transition-all duration-300 ${
                      isSubmitting
                        ? 'bg-gray-600 cursor-not-allowed text-gray-300'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-black'
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'error' && (
                    <p className="text-red-500 text-center mt-2">Failed to send message. Please try again later.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;